import { inject, Injectable } from '@angular/core';
import { UserService } from '@ddv/entitlements';
import { ManagerService } from '@ddv/layout';
import {
    DdvDate,
    DdvDateTime,
    UserPreferences,
    ExportDatasetInfo,
    ExportWidgetInfo,
    ExportFormat,
    FuzzyDate,
    FuzzyDates,
    AppWidgetState,
    MetadataLookup,
    WidgetData,
    UserDefinedFieldType,
    VisualizationConfigs,
    ExportDatasetWidgetInfo,
} from '@ddv/models';
import { FuzzyDatesService } from '@ddv/reference-data';
import { Observable, Subject } from 'rxjs';

import { FilteredDataService } from './filtered-data.service';
import { ExportableData } from '../models/exportable-data';

export interface ExportRequest {
    datasets: ExportDatasetInfo[];
    dashboardName: string;
    exportWidgetInfos: Partial<ExportWidgetInfo>[];
    exportFormat: ExportFormat;
    clientCode: string;
}

@Injectable()
export class WidgetExportService {
    private readonly managerService = inject(ManagerService);
    private readonly filteredDataService = inject(FilteredDataService);
    private readonly fuzzyDatesService = inject(FuzzyDatesService);
    private readonly userService = inject(UserService);

    public readonly exportRequested: Observable<ExportRequest>;

    private readonly exportRequestedSubject: Subject<ExportRequest> = new Subject<ExportRequest>();
    private includeCrosstalkKeys = false;
    private fuzzyDates: FuzzyDates | undefined;
    private userPreferences: UserPreferences | undefined;
    private exportData: Record<string, ExportableData> | undefined;

    constructor() {
        this.fuzzyDatesService.fuzzyDates().subscribe((fuzzyDates: FuzzyDates) => {
            this.fuzzyDates = fuzzyDates;
        });

        this.userService.userPreferences$.subscribe((userPreferences: UserPreferences) => {
            this.userPreferences = userPreferences;
        });

        this.filteredDataService.filteredData$.subscribe((filteredData) => {
            this.exportData = filteredData;
        });

        this.exportRequested = this.exportRequestedSubject.asObservable();
    }

    updateIncludeCrosstalkKeys(includeCrosstalkKeys: boolean): void {
        this.includeCrosstalkKeys = includeCrosstalkKeys;
    }

    shouldIncludeCrosstalkKeys(widget: ExportWidgetInfo | AppWidgetState | undefined): boolean {
        return !!(this.includeCrosstalkKeys &&
            widget?.namedQuery?.normalizeConversableType() &&
            widget?.currentVisualization === 'ADVANCED_GRID');
    }

    setUserDefinedFieldExportValue(datum: WidgetData, colId: string, type: UserDefinedFieldType): string | number | boolean | null {
        // eslint-disable-next-line eqeqeq
        const value: string | number | boolean | null = (typeof datum[colId] === 'object' && datum[colId] !== null) ?
            datum[colId].value :
            datum[colId];

        if (this.userDefinedFieldColumnHasValue(value)) {
            return value as string | number | boolean;
            // As nulls for number columns are transformed into zeros in the UI, we want the same in the export
            // eslint-disable-next-line  @typescript-eslint/prefer-optional-chain
        } else if (type === 'decimal' && (datum == null || datum[colId] == null || datum[colId].value == null)) {
            return 0;
        }

        return '';
    }

    exportWidgetFilteredCSV(widgetId: number | string, metadata: MetadataLookup): void {
        if (!this.exportData || !Object.keys(this.exportData).length) {
            return;
        }

        const widgetPrefs = this.managerService.getWidgetPreferences(widgetId);
        const currentViz = widgetPrefs?.currentVisualization;
        const showSubTotal = !!widgetPrefs?.visualizationConfigs.find((config) => config.visualizationType === currentViz)?.showSubTotal;

        if (currentViz === 'ADVANCED_GRID') {
            this.addUserDefinedFieldsToColumns(widgetPrefs, currentViz, this.exportData, widgetId);
        }

        const columns = this.getColumns(this.exportData, widgetId);

        if (columns.length) {
            this.addDateColumnsForCSVExport(this.exportData[widgetId], widgetId.toString(), columns);
        }

        const widgets: ExportDatasetWidgetInfo[] = [{
            widgetId: widgetId.toString(),
            widgetName: `${this.exportData[widgetId].widgetName}`,
            columnNames: columns,
            queryType: this.exportData[widgetId].queryType,
            dateFrom: this.exportData[widgetId] ? this.fuzzyDateCheck(this.exportData[widgetId].startDate, true) : undefined,
            dateTo: this.exportData[widgetId] ? this.fuzzyDateCheck(this.exportData[widgetId].endDate) : undefined,
            extractionDateTime: DdvDateTime.now.toShortDateAndTime(),
            groupers: this.exportData[widgetId].groupers ?? [],
            dataTypeOverrides: {},
            showSubTotal,
        }];

        const dashboardName = this.managerService.getWorkspace()?.name.split('_')[1] ?? '';
        const exportWidgetInfos = this.getExportWidgetInfos(widgetPrefs);
        const exportFormat = 'CSV';
        const clientCode = this.userPreferences?.clientCode ?? '';

        this.exportRequestedSubject.next({
            datasets: [{
                data: this.exportData[widgetId].data,
                metadata,
                summary: this.exportData[widgetId].summary,
                widgets,
            }],
            dashboardName,
            exportWidgetInfos,
            exportFormat,
            clientCode,
        });

        this.filteredDataService.clearFilteredData({});
    }

    fuzzyDateCheck(dateToCheck: string | undefined, isStartDate = false): string {
        const date = (!dateToCheck || dateToCheck.toUpperCase() === 'USE DEFAULTS' || dateToCheck.toUpperCase() === '' ?
            (isStartDate ? this.userPreferences?.startDate : this.userPreferences?.endDate) :
            dateToCheck) ?? '';
        const fuzzyDate = this.getFuzzyDate(date);
        return fuzzyDate ? DdvDate.fromISOFormat(fuzzyDate.value).toUSFormat() : date;
    }

    getFuzzyDate(value: string): FuzzyDate | undefined {
        return this.fuzzyDates?.from.find((fuzzyDate) => value && fuzzyDate.name.toLocaleUpperCase() === value.toUpperCase());
    }

    addDateColumnsForCSVExport(data: ExportableData, widgetId: string, columns: string[]): string[] {
        const dateColumns: string[] = [];
        if (data.queryType && ['Recon', 'Checklists'].indexOf(data.queryType) === -1) {
            dateColumns.push('From Date');
        }
        dateColumns.push(...['To Date', 'Time of Extract']);

        const currentWidget = this.managerService.getWorkspace()?.extraParameters?.widgets.find((widget: AppWidgetState) => {
            return widget.id?.toString() === widgetId;
        });

        // put the inserted date columns before the first crosstalk key column
        if (currentWidget && this.shouldIncludeCrosstalkKeys(currentWidget) && data.crosstalkImportColumns) {
            const firstCrosstalkKeyColumn = data.crosstalkImportColumns[0];
            const indexOfFirstCrosstalkKeyColumn = columns.lastIndexOf(firstCrosstalkKeyColumn);
            if (indexOfFirstCrosstalkKeyColumn > -1) {
                columns.splice(indexOfFirstCrosstalkKeyColumn, 0, ...dateColumns);
            }
        } else {
            columns.push(...dateColumns);
        }

        return columns;
    }

    isUserDefinedFieldColumn(name: string | undefined): boolean {
        return !!name?.startsWith('udf_');
    }

    private addUserDefinedFieldsToColumns(
        widgetPrefs: AppWidgetState | null | undefined,
        currentViz: string,
        exportData: Record<string, ExportableData>,
        widgetId: number | string,
    ): void {
        const udfColumns: { name: string, type: UserDefinedFieldType }[] = [];

        widgetPrefs?.visualizationConfigs
            .find((viz: VisualizationConfigs) => viz.visualizationType === currentViz)
            ?.configs
            ?.values
            ?.filter((vizConf) => this.isUserDefinedFieldColumn(vizConf.value))
            .forEach((udfColumn) => udfColumns.push({
                name: udfColumn.customName ?? udfColumn.label ?? '',
                type: udfColumn.datatype as UserDefinedFieldType,
            }));

        if (udfColumns.length) {
            udfColumns.forEach((udf) => {
                exportData[widgetId].data.forEach((datum: WidgetData) => {
                    datum[udf.name] = this.setUserDefinedFieldExportValue(datum, udf.name, udf.type);
                });
            });
        }
    }

    private getColumns(exportData: Record<string, ExportableData>, id: string | number): string[] {
        return Object.keys(exportData[id]?.data[0] || []).reduce((columns: string[], column) => {
            if (column !== 'conversationId') {
                columns.push(column.replace('___Grouper___', ''));
            }

            return columns;
        }, []);
    }

    private getExportWidgetInfos(widgetPrefs: AppWidgetState | null | undefined): Partial<ExportWidgetInfo>[] {
        return [{
            id: widgetPrefs?.id ?? 0,
            name: widgetPrefs?.customDisplayName ?? widgetPrefs?.name ?? '',
        }];
    }

    private userDefinedFieldColumnHasValue(value: string | number | boolean | null): boolean {
        if (typeof value === 'boolean') {
            return true;
        }
        return value != null && !!(value || value === 0);
    }
}
